import React from "react";
import { useDispatch } from "react-redux";
import { Field, InjectedFormProps, reduxForm } from "redux-form";

import { TextField, SubmitBtn } from "capitalrx-components";

import { pharmacySearchActions } from "@/App/actions";
import ClearFieldsButton from "@/App/components/ClearFieldsButton";
import SectionContainer from "@/App/components/SectionContainer";

import { logEvent, ANALYTIC_EVENTS } from "@/App/utils/analytics";

import { GeneralTypes } from "@/types";

type Props = InjectedFormProps;

const FORM_NAME = "PharmacySearchForm";

const INITIAL_QUERY_OPTIONS: GeneralTypes.QueryOptions = {
  order_by: [{ npi: GeneralTypes.SortOrder.Asc }],
  page_number: 1,
  results_per_page: 20,
};

const PharmacySearchForm: React.FC<Props> = (props: Props) => {
  const { handleSubmit, submitting = false } = props;

  const { searchForPharmacy } = pharmacySearchActions;

  const dispatch = useDispatch();

  const handleFormSubmit = (data: GeneralTypes.QueryOptions): void => {
    // on form submit, reset the pagination/sorting to default
    dispatch(
      searchForPharmacy({
        data: { ...INITIAL_QUERY_OPTIONS, ...data },
        formName: FORM_NAME,
      }),
    );

    // capture button press
    logEvent(ANALYTIC_EVENTS.TEST_CLAIM_PHARMACY.searchForPharmacy);

    // also wants to be able to capture which text fields held data
    const fieldToAnalyticsKey: { [index: string]: string } = {
      npi: ANALYTIC_EVENTS.TEST_CLAIM_PHARMACY.searchCriteriaFieldServiceProvider,
      name: ANALYTIC_EVENTS.TEST_CLAIM_PHARMACY.searchCriteriaFieldProviderName,
      address: ANALYTIC_EVENTS.TEST_CLAIM_PHARMACY.searchCriteriaFieldStreetAddress,
      zip: ANALYTIC_EVENTS.TEST_CLAIM_PHARMACY.searchCriteriaFieldZip,
    };
    Object.keys(data).forEach((key) => {
      logEvent(fieldToAnalyticsKey[key]);
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <SectionContainer headerTitle="Search Criteria" abilityToClose={true}>
        <div className="row">
          <div className="col">
            <Field
              component={TextField}
              id="npi"
              data-testid="test__npi"
              name="npi"
              label="Service Provider ID (NPI)"
              type="text"
            />
          </div>
          <div className="col">
            <Field component={TextField} id="name" data-testid="test__pharmacy-search-name" name="name" label="Provider Name" type="text" />
          </div>
        </div>
        <div className="row">
          <div className="col-8">
            <Field
              component={TextField}
              id="address"
              data-testid="test__pharmacy-search-address"
              name="address"
              label="Street Address"
              type="text"
            />
          </div>
          <div className="col-4">
            <Field component={TextField} id="zip" data-testid="test__pharmacy-search-zip" name="zip" label="Zip Code" type="text" />
          </div>
        </div>
        <div className="row caprx-h-center-text">
          <SubmitBtn fullWidth={false} submitting={submitting} data-testid="test__pharmacy-search-button">
            SEARCH FOR PHARMACY
          </SubmitBtn>
        </div>
        <ClearFieldsButton formName={FORM_NAME} />
      </SectionContainer>
    </form>
  );
};

export default reduxForm({ form: FORM_NAME })(PharmacySearchForm);
